/**
 * Task #2617 — Validatori fiscali italiani: FONTE DI VERITÀ UNICA.
 *
 * Centralizzati in `@easyflow/shared` così che frontend (`lib/italian-fiscal.ts`),
 * backend (`utils/italian-fiscal-validators.ts`), `ui-frontend` (form anagrafico
 * condiviso) e gli schemi Zod (`pos/admin-inputs.ts`, `types/api.ts`) usino la
 * STESSA implementazione → zero divergenza FE vs BE per costruzione.
 *
 * - Partita IVA: 11 cifre + checksum (Luhn modificato per la P.IVA italiana).
 * - Codice Fiscale: 16 alfanumerici (persona fisica) o 11 cifre (persona giuridica = P.IVA).
 * - Email/PEC: regex base RFC-lite sufficiente per validare un recapito.
 * - CAP, sigla provincia, codice SDI, URL: validazioni di formato robuste.
 * - IBAN: lunghezza per-paese + checksum mod-97 (ISO 13616).
 *
 * Pure functions, nessuna eccezione sollevata.
 */

const VAT_RE = /^\d{11}$/;
const CF_PERSON_RE = /^[A-Z0-9]{16}$/;
const CF_LEGAL_RE = /^\d{11}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CAP_RE = /^\d{5}$/;
const SDI_RE = /^[A-Z0-9]{6,7}$/;

/**
 * Verifica P.IVA italiana: 11 cifre + checksum.
 * Somma cifre dispari + (per le pari, somma cifre del doppio).
 * Cifra di controllo = (10 - somma % 10) % 10.
 */
export function isValidItalianVat(vat?: string | null): boolean {
  const v = (vat || '').trim();
  if (!VAT_RE.test(v)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    const digit = parseInt(v[i] ?? '', 10);
    if (i % 2 === 0) {
      sum += digit;
    } else {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(v[10] ?? '', 10);
}

/**
 * Verifica Codice Fiscale italiano: 16 alfanumerici (persona fisica)
 * oppure 11 cifre (persona giuridica = P.IVA, con checksum P.IVA).
 *
 * Per le persone fisiche si valida il solo formato: il checksum del CF
 * (modulo 26) richiede la tabella di conversione completa e non viene
 * calcolato qui per non bloccare CF formalmente corretti.
 */
export function isValidItalianFiscalCode(cf?: string | null): boolean {
  const f = (cf || '').trim().toUpperCase();
  if (CF_PERSON_RE.test(f)) return true;
  if (CF_LEGAL_RE.test(f)) return isValidItalianVat(f);
  return false;
}

/**
 * Verifica il Codice Fiscale di una PERSONA FISICA: esattamente 16 caratteri
 * alfanumerici. A differenza di `isValidItalianFiscalCode` NON ammette la forma
 * a 11 cifre (persona giuridica), perché un cliente B2C è sempre persona fisica
 * e il cross-check con la data di nascita richiede il CF a 16 caratteri.
 */
export function isValidPersonFiscalCode(cf?: string | null): boolean {
  const f = (cf || '').trim().toUpperCase();
  return CF_PERSON_RE.test(f);
}

/** True se il soggetto possiede almeno uno tra P.IVA o Codice Fiscale validi. */
export function hasValidFiscalId(vat?: string | null, cf?: string | null): boolean {
  return isValidItalianVat(vat) || isValidItalianFiscalCode(cf);
}

/** Validazione email base (usata anche per la PEC: stesso formato). */
export function isValidEmail(email?: string | null): boolean {
  const e = (email || '').trim();
  if (!e) return false;
  return EMAIL_RE.test(e);
}

/** CAP italiano: esattamente 5 cifre. */
export function isValidCap(cap?: string | null): boolean {
  const c = (cap || '').trim();
  return CAP_RE.test(c);
}

/** Sigle provincia italiane (110 sigle). */
const PROVINCE_CODES = new Set<string>([
  'AG', 'AL', 'AN', 'AO', 'AR', 'AP', 'AT', 'AV', 'BA', 'BT', 'BL', 'BN', 'BG',
  'BI', 'BO', 'BZ', 'BS', 'BR', 'CA', 'CL', 'CB', 'CI', 'CE', 'CT', 'CZ', 'CH',
  'CO', 'CS', 'CR', 'KR', 'CN', 'EN', 'FM', 'FE', 'FI', 'FG', 'FC', 'FR', 'GE',
  'GO', 'GR', 'IM', 'IS', 'SP', 'LT', 'LE', 'LC', 'LI', 'LO', 'LU', 'MC', 'MN',
  'MS', 'MT', 'VS', 'ME', 'MI', 'MO', 'MB', 'NA', 'NO', 'NU', 'OG', 'OT', 'OR',
  'PD', 'PA', 'PR', 'PV', 'PG', 'PU', 'PE', 'PC', 'PI', 'PT', 'PN', 'PZ', 'PO',
  'RG', 'RA', 'RC', 'RE', 'RI', 'RN', 'RM', 'RO', 'SA', 'SS', 'SV', 'SI', 'SR',
  'SO', 'SU', 'TA', 'TE', 'TR', 'TO', 'TP', 'TN', 'TV', 'TS', 'UD', 'VA', 'VE',
  'VB', 'VC', 'VR', 'VV', 'VI', 'VT',
]);

/** Sigla provincia italiana: 2 lettere presenti nell'elenco ufficiale. */
export function isValidProvince(province?: string | null): boolean {
  const p = (province || '').trim().toUpperCase();
  return PROVINCE_CODES.has(p);
}

/**
 * Codice destinatario SDI (fattura elettronica): 7 caratteri alfanumerici
 * per destinatari privati, 6 per la Pubblica Amministrazione.
 */
export function isValidSdiCode(code?: string | null): boolean {
  const c = (code || '').trim().toUpperCase();
  return SDI_RE.test(c);
}

/** URL http/https valido. */
export function isValidUrl(url?: string | null): boolean {
  const u = (url || '').trim();
  if (!u) return false;
  try {
    const parsed = new URL(u);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Lunghezza IBAN per codice paese (ISO 13616). Limitata ai paesi rilevanti +
 * tutti i principali SEPA; per i paesi non in tabella si applica solo il range
 * generico 15–34 e il checksum mod-97.
 */
const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  IT: 27, SM: 27, AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24,
  DE: 22, DK: 18, EE: 20, ES: 24, FI: 18, FR: 27, GB: 22, GR: 27, HR: 21,
  HU: 28, IE: 22, IS: 26, LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MT: 31,
  NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24, SI: 19, SK: 24, VA: 22,
};

/**
 * Verifica IBAN: formato `^[A-Z]{2}\d{2}[A-Z0-9]+$`, lunghezza coerente con il
 * paese (se noto) e checksum mod-97 == 1 (ISO 13616). Tollera spazi.
 */
export function isValidIban(iban?: string | null): boolean {
  const raw = (iban || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(raw)) return false;
  if (raw.length < 15 || raw.length > 34) return false;
  const country = raw.slice(0, 2);
  const expected = IBAN_LENGTHS[country];
  if (expected !== undefined && raw.length !== expected) return false;
  // Sposta i primi 4 char in coda e converti lettere in numeri (A=10..Z=35).
  const rearranged = raw.slice(4) + raw.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    const value = code >= 65 && code <= 90 ? (code - 55).toString() : ch;
    for (const d of value) {
      remainder = (remainder * 10 + (d.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
}
